import { LocationContext, WeatherData, LocationInfo, Coordinates } from '../types/location';
import { OpenWeatherMapResponse } from '../types/weather';

export interface FormattedLocationContext {
  coordinates: string;
  location: string;
  weather: string;
  contextSummary: string;
}

export function formatLocationContext(locationContext: LocationContext): FormattedLocationContext {
  const { coordinates, locationInfo, weather } = locationContext;

  // Format coordinates
  const coordinatesStr = `${coordinates.latitude.toFixed(4)}°N, ${Math.abs(coordinates.longitude).toFixed(4)}°${coordinates.longitude >= 0 ? 'E' : 'W'}`;

  // Format location information
  let locationStr = 'Location not specified';
  if (locationInfo) {
    const parts: string[] = [];
    if (locationInfo.city) parts.push(locationInfo.city);
    if (locationInfo.state) parts.push(locationInfo.state);
    if (locationInfo.country) parts.push(locationInfo.country);
    
    if (parts.length > 0) {
      locationStr = parts.join(', ');
      if (locationInfo.zipCode) {
        locationStr += ` ${locationInfo.zipCode}`;
      }
    }
  }

  // Format weather information
  let weatherStr = 'Weather information not available';
  if (weather) {
    const condition = weather.conditions[0]?.description || 'unknown';
    const temp = Math.round(weather.temperature);
    const feelsLike = Math.round(weather.feelsLike);
    const humidity = weather.humidity;
    const windSpeed = Math.round(weather.windSpeed * 3.6); // Convert m/s to km/h
    
    weatherStr = `${condition}, ${temp}°C (feels like ${feelsLike}°C), ${humidity}% humidity`;
    if (windSpeed > 0) {
      weatherStr += `, wind ${windSpeed} km/h`;
    }
  }

  // Create a comprehensive context summary
  const contextSummary = `Current location: ${locationStr} (${coordinatesStr}). Weather: ${weatherStr}.`;

  return {
    coordinates: coordinatesStr,
    location: locationStr,
    weather: weatherStr,
    contextSummary
  };
}

export function transformWeatherData(apiResponse: OpenWeatherMapResponse): WeatherData {
  return {
    temperature: apiResponse.main.temp,
    feelsLike: apiResponse.main.feels_like,
    humidity: apiResponse.main.humidity,
    pressure: apiResponse.main.pressure,
    windSpeed: apiResponse.wind.speed,
    windDirection: apiResponse.wind.deg,
    visibility: apiResponse.visibility,
    conditions: apiResponse.weather.map(condition => ({
      main: condition.main,
      description: condition.description,
      icon: condition.icon
    })),
    timestamp: Date.now()
  };
}

export function extractLocationInfo(apiResponse: OpenWeatherMapResponse): LocationInfo {
  return {
    city: apiResponse.name,
    country: apiResponse.sys.country,
    timezone: `UTC${apiResponse.timezone >= 0 ? '+' : ''}${Math.floor(apiResponse.timezone / 3600)}:${String(Math.abs((apiResponse.timezone % 3600) / 60)).padStart(2, '0')}`
  };
}

export function formatWeatherForConversation(weather: WeatherData): string {
  const condition = weather.conditions[0]?.description || 'unknown conditions';
  const temp = Math.round(weather.temperature);
  const feelsLike = Math.round(weather.feelsLike);
  
  let description = `The current weather is ${condition} with a temperature of ${temp}°C`;
  
  if (Math.abs(temp - feelsLike) > 2) {
    description += ` (feels like ${feelsLike}°C)`;
  }
  
  description += `. Humidity is at ${weather.humidity}%`;
  
  if (weather.windSpeed > 2) {
    const windKmh = Math.round(weather.windSpeed * 3.6);
    description += ` with winds at ${windKmh} km/h`;
  }
  
  return description + '.';
}

export function shouldUpdateWeather(lastUpdate: number, cacheExpiry: number = 10 * 60 * 1000): boolean {
  return Date.now() - lastUpdate > cacheExpiry;
}

export function formatCoordinatesForAPI(coordinates: Coordinates): { latitude: number; longitude: number } {
  return {
    latitude: parseFloat(coordinates.latitude.toFixed(6)),
    longitude: parseFloat(coordinates.longitude.toFixed(6))
  };
}